import { useState, useEffect } from "react";
import api from "@/services/api";
import {
  Building2,
  User,
  Mail,
  Phone,
  Briefcase,
  MapPin,
  FileText,
  Hash,
  Tag,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const initialForm = {
  empresa: "",
  nombre: "",
  email: "",
  telefono: "",
  cargo: "",
  sector: "",
  nif_cif: "",
  direccion: "",
  ciudad: "",
  notas: "",
};

export default function ContactoModal({ open, onClose, contacto }) {
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);
  const isEditing = !!contacto;

  useEffect(() => {
    if (open && contacto) {
      setForm({
        empresa: contacto.empresa || "",
        nombre: contacto.nombre || "",
        email: contacto.email || "",
        telefono: contacto.telefono || "",
        cargo: contacto.cargo || "",
        sector: contacto.sector || "",
        nif_cif: contacto.nif_cif || "",
        direccion: contacto.direccion || "",
        ciudad: contacto.ciudad || "",
        notas: contacto.notas || "",
      });
    } else if (open) {
      setForm(initialForm);
    }
  }, [open, contacto]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!form.empresa.trim()) {
      toast.error("Empresa es requerido");
      return;
    }
    if (!form.nombre.trim()) {
      toast.error("Nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      if (isEditing) {
        const id = contacto.contacto_id || contacto._id;
        await api.patch(`/contactos/${id}`, form);
        toast.success("Contacto actualizado");
      } else {
        await api.post('/contactos', form);
        toast.success("Contacto creado");
      }
      onClose(true);
    } catch (error) {
      toast.error(
        error.response?.data?.detail || "Error al guardar contacto"
      );
    } finally {
      setSaving(false);
    }
  };

  const Field = ({ icon: Icon, label, field, type = "text", placeholder }) => (
    <div className="space-y-1.5">
      <Label className="text-xs font-medium theme-text-secondary">{label}</Label>
      <div className="relative">
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 theme-text-muted" />
        <Input
          type={type}
          value={form[field]}
          onChange={(e) => handleChange(field, e.target.value)}
          placeholder={placeholder}
          className="pl-10 theme-bg theme-text text-sm"
          style={{ borderColor: "var(--theme-border)" }}
        />
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={() => onClose(false)}>
      <DialogContent
        className="theme-bg-secondary max-w-lg max-h-[85vh] overflow-y-auto"
        style={{ border: "1px solid var(--theme-border)" }}
      >
        <DialogHeader>
          <DialogTitle className="theme-text text-lg">
            {isEditing ? "Editar Contacto" : "Nuevo Contacto"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 mt-2">
          {/* Empresa section */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold theme-text-muted uppercase tracking-wider">
              Empresa
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field icon={Building2} label="Empresa *" field="empresa" placeholder="Nombre de la empresa" />
              </div>
              <Field icon={Hash} label="NIF/CIF" field="nif_cif" placeholder="B12345678" />
              <Field icon={Tag} label="Sector" field="sector" placeholder="Tecnologia, Salud..." />
              <Field icon={MapPin} label="Direccion" field="direccion" placeholder="Calle, numero..." />
              <Field icon={MapPin} label="Ciudad" field="ciudad" placeholder="Madrid, Barcelona..." />
            </div>
          </div>

          {/* Persona section */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold theme-text-muted uppercase tracking-wider">
              Persona de contacto
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Field icon={User} label="Nombre *" field="nombre" placeholder="Nombre completo" />
              </div>
              <Field icon={Mail} label="Email" field="email" type="email" placeholder="email@empresa.com" />
              <Field icon={Phone} label="Telefono" field="telefono" type="tel" placeholder="+34 600 000 000" />
              <div className="col-span-2">
                <Field icon={Briefcase} label="Cargo" field="cargo" placeholder="Director, Gerente..." />
              </div>
            </div>
          </div>

          {/* Notas */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium theme-text-secondary">Notas</Label>
            <Textarea
              value={form.notas}
              onChange={(e) => handleChange("notas", e.target.value)}
              placeholder="Notas adicionales sobre este contacto..."
              className="theme-bg theme-text text-sm min-h-[80px]"
              style={{ borderColor: "var(--theme-border)" }}
            />
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="ghost"
              onClick={() => onClose(false)}
              className="theme-text-secondary"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-cyan-600 hover:bg-cyan-700 text-white"
            >
              {saving ? "Guardando..." : isEditing ? "Guardar cambios" : "Crear contacto"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
